import {
  Alert,
  Autocomplete,
  Box,
  DialogTitle,
  type FilterOptionsState,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { CheckPermissions } from '@/components/CheckPermissions';
import FileUploader from '@/components/FileUploader/FileUploader';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  ACCEPTED_FILES_CSV,
  ACCEPTED_FILES_EXCEL,
  ACCEPTED_FILES_IMAGE,
  ACCEPTED_FILES_PDF,
  ACCEPTED_FILES_WORD
} from '@/constants/common';
import useConfirm from '@/hooks/useConfirm';
import { parse_and_replace_links } from '@/utils/Common.utils';

import useAnnouncements from '../Announcement.hooks';
import type {
  Announcement,
  AnnouncementOrganizationType,
  AttachmentFile
} from '../Announcements.types';
import { handleAttachmentClick } from '../Announcements.utils';
import {
  AnnouncementDialogType,
  CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES
} from './AnnouncementDialog.constants';
import useAnnouncementDialog from './AnnouncementDialog.hooks';
import useStyles from './AnnouncementDialog.styles';
import createOrgsArray, {
  isAnnouncementChanged
} from './AnnouncementDialog.utils';
import AnnouncementDialogMarkdown from './AnnouncementDialogMarkdown';
import AnnouncementDialogScheduling from './AnnouncementDialogScheduling';

const getOrgFilterOptions = (
  options: AnnouncementOrganizationType[],
  state: FilterOptionsState<AnnouncementOrganizationType>
) => {
  return options.filter((option) => {
    if (state.inputValue) {
      return `${option.name} ${option.label}`
        .toLowerCase()
        .includes(state.inputValue.toLowerCase());
    }
    return true;
  });
};

const AnnouncementDialog = () => {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const {
    addAttachment,
    addAttachmentError,
    addAttachmentLoading,
    addAttachmentSuccess,
    createAnnouncement,
    data,
    deleteAnnouncement,
    deleteAttachment,
    isSuccess,
    organizations,
    publishAnnouncement,
    selectedAnnouncement,
    updateAnnouncement,
    updateAnnouncementLoading,
    userIsInternal
  } = useAnnouncements();

  const {
    handleOrgSelectChange,
    form,
    open,
    orgGroups,
    selectedOptions,
    setOpen,
    setOrgGroups,
    setSelectedOptions,
    type,
    industries
  } = useAnnouncementDialog();

  const { control, handleSubmit, setValue, getValues, trigger } = form;

  const { ConfirmationModal: AnnouncementDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Draft Announcement?',
      message: 'Please confirm that you want to delete this draft.',
      button: 'Delete'
    });

  const handleDeleteAnnouncement = async () => {
    const confirmed = await confirmDelete();
    if (confirmed) {
      deleteAnnouncement.mutate(selectedAnnouncement.id);
      setOpen(false);
    }
  };

  const handleDeleteAttachment = (key: string) => {
    const deleteParams = {
      key,
      announcementId: selectedAnnouncement.id
    };
    deleteAttachment.mutate(deleteParams);
  };

  const handlePublish = () => {
    // publishing does not save any changes. Check if changes are saved before publishing
    if (!isAnnouncementChanged(selectedAnnouncement, getValues())) {
      setErrorMsg('Please save changes before publishing.');
      return;
    }

    publishAnnouncement.mutate({
      announcement_id: selectedAnnouncement.id
    });
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
    form.reset(CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES);
    setSelectedOptions([]);
    setOrgGroups((prevGroups: AnnouncementOrganizationType[]) => {
      const updatedGroups = [...prevGroups];
      prevGroups.forEach(
        (_group: AnnouncementOrganizationType, index: number) => {
          updatedGroups[index].selected = false;
        }
      );
      return prevGroups;
    });
  };

  // const acceptedFileTypes = ['pdf', 'jpeg', 'png', 'jpg'];

  const onSubmit = async () => {
    const announcement = getValues();
    if (dayjs(announcement.scheduled_at).isValid()) {
      announcement.scheduled_at = dayjs(announcement.scheduled_at)
        .utc()
        .format();
    }
    if (type === AnnouncementDialogType.CREATE) {
      createAnnouncement(announcement);

      // Close dialog if no attachments, otherwise close after adding attachments
      if (getValues('attachments').length === 0) {
        setOpen(false);
        navigate('/announcements/drafts');
      }
      return;
    }

    if (type === AnnouncementDialogType.EDIT) {
      updateAnnouncement(announcement as Announcement);

      // Array of keys of attachments in selectedValues
      const selectedValuesAttachments =
        selectedAnnouncement.attachments?.map((file) => file.key) || [];

      // Array of keys of attachments in form
      const currentValueAttachments = getValues('attachments').map(
        (file) => file.key
      );

      const deleteKeys = selectedValuesAttachments.filter(
        (file) => !currentValueAttachments.includes(file)
      ); // Array of keys of attachments to be deleted
      const deleteFiles = (selectedAnnouncement.attachments || []).filter(
        (file) => deleteKeys.includes(file.key)
      ); // Array of attachments to be deleted

      // Delete attachments that are not in the form
      if (deleteFiles.length > 0) {
        deleteFiles.forEach((file) => {
          handleDeleteAttachment(file.key);
        });
      }

      const addFiles = getValues('attachments').filter(
        (file) => !selectedValuesAttachments.includes(file.key)
      ); // Array of attachments to be added

      if (addFiles.length > 0) {
        const newAttachmentData = {
          id: selectedAnnouncement.id,
          attachments: addFiles
        };
        addAttachment(newAttachmentData);
      } else {
        setOpen(false);
      }
    }
  };

  /**
   * Add attachments to announcement after creating
   */
  useEffect(() => {
    if (isSuccess && getValues('attachments').length > 0) {
      const newAttachmentData = {
        id: data?.data.id,
        attachments: getValues('attachments')
      };
      addAttachment(newAttachmentData);
    }
  }, [isSuccess, data]);

  /**
   * Close dialog after attachments are successfully added
   */
  useEffect(() => {
    if (addAttachmentSuccess) {
      setOpen(false);
    }
  }, [addAttachmentSuccess]);

  // All options are the combination of orgGroups and organizations
  const allOptions = [...orgGroups, ...industries, ...organizations];

  // Saves org_list value to form when selectedOptions changes
  useEffect(() => {
    if (selectedOptions.length > 0) {
      setValue(
        'org_list',
        JSON.stringify(createOrgsArray(selectedOptions, organizations))
      );
      trigger('org_list');
    } else {
      setValue('org_list', '');
    }
  }, [selectedOptions, organizations, setValue]);

  // Reset selected orgs when dialog is opened
  useEffect(() => {
    if (open) {
      setOrgGroups((prevGroups: AnnouncementOrganizationType[]) => {
        const updatedGroups = [...prevGroups];
        prevGroups.forEach(
          (_group: AnnouncementOrganizationType, index: number) => {
            updatedGroups[index].selected = false;
          }
        );
        return prevGroups;
      });
    }
  }, [open]);

  return (
    <Dialog
      scroll="paper"
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {`${type} Announcement ${userIsInternal && type !== AnnouncementDialogType.CREATE ? `#${selectedAnnouncement.id}` : ''}`}
      </DialogTitle>
      <DialogContent style={{ paddingTop: 10 }}>
        {userIsInternal && (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2} sx={{ pb: 1 }}>
                {errorMsg && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMsg}
                  </Alert>
                )}
                <div>
                  <Controller
                    control={control}
                    name="org_list"
                    rules={{
                      required: true,
                      pattern: /.+/
                    }}
                    render={({ formState }) => (
                      <FormControl sx={{ width: '100%' }}>
                        <Typography className={classes.orgSelectLabel}>
                          Select Organizations{' '}
                          {type !== AnnouncementDialogType.VIEW && '*'}
                        </Typography>
                        {type !== AnnouncementDialogType.VIEW && (
                          <Autocomplete
                            multiple
                            filterOptions={getOrgFilterOptions}
                            className={classes.orgAutocomplete}
                            id="autocomplete-input"
                            onChange={handleOrgSelectChange}
                            options={
                              allOptions as AnnouncementOrganizationType[]
                            }
                            readOnly={type === AnnouncementDialogType.VIEW}
                            groupBy={(option: AnnouncementOrganizationType) =>
                              option.group ? option.group : 'Organizations'
                            }
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => {
                              return (
                                <li
                                  style={{
                                    backgroundColor: option.selected
                                      ? 'var(--mui-palette-secondary-light)'
                                      : ''
                                  }}
                                  {...props}
                                >
                                  <Stack
                                    direction="row"
                                    sx={{
                                      width: '100%',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}
                                  >
                                    {option.name}
                                    <Typography color="textSecondary">
                                      {option.label}
                                    </Typography>
                                  </Stack>
                                </li>
                              );
                            }}
                            isOptionEqualToValue={(option, value) => {
                              return option.id === value.id;
                            }}
                            value={
                              selectedOptions as AnnouncementOrganizationType[]
                            }
                            renderInput={(params) => (
                              <TextField
                                key={params.id}
                                {...params}
                                error={!!formState.errors.org_list}
                              />
                            )}
                          />
                        )}
                        {type === AnnouncementDialogType.VIEW && (
                          <span>
                            {selectedOptions.map((option) => (
                              <Chip
                                key={option.id}
                                label={option.name}
                                sx={{ mr: 1, mt: 1 }}
                              />
                            ))}
                          </span>
                        )}
                      </FormControl>
                    )}
                  />
                </div>
                <div>
                  <InputLabel htmlFor="title-input">
                    Title {type !== AnnouncementDialogType.VIEW && '*'}
                  </InputLabel>
                  {type !== AnnouncementDialogType.VIEW && (
                    <Controller
                      control={control}
                      name="title"
                      rules={{
                        required: true
                      }}
                      render={({ field, formState }) => (
                        <FormControl fullWidth>
                          <TextField
                            id="title-input"
                            error={!!formState.errors.title}
                            {...field}
                          />
                        </FormControl>
                      )}
                    />
                  )}
                  {type === AnnouncementDialogType.VIEW && (
                    <p>{selectedAnnouncement.title}</p>
                  )}
                </div>
                <AnnouncementDialogMarkdown type={type} />
                <CheckPermissions permissions={PERMISSIONS.ANNOUNCEMENT_CREATE}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Stack spacing={2}>
                      <AnnouncementDialogScheduling type={type} />
                    </Stack>
                    {type !== AnnouncementDialogType.VIEW && (
                      <div>
                        <FileUploader
                          acceptedTypes={[
                            ...ACCEPTED_FILES_CSV,
                            ...ACCEPTED_FILES_EXCEL,
                            ...ACCEPTED_FILES_IMAGE,
                            ...ACCEPTED_FILES_PDF,
                            ...ACCEPTED_FILES_WORD
                          ]}
                          errorMessage="Please select a supported file type (PDF, DOCX, XLSX, CSV, JPG, PNG) and ensure each file is under 10MB."
                          isRequired={false}
                          selectedFileListPlacement="left"
                          titleButton="button"
                          titleButtonTooltip="PDF, DOCX, XLSX, CSV, JPG, PNG (max 10MB)"
                          titleButtonVariant="outlined"
                        />
                        {addAttachmentError && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            There was an error adding one or more attachments
                          </Alert>
                        )}
                      </div>
                    )}
                  </Stack>
                </CheckPermissions>
                {type === AnnouncementDialogType.VIEW && (
                  <Stack sx={{ alignItems: 'flex-start' }} spacing={2}>
                    {getValues('attachments').map((file: AttachmentFile) => (
                      <Button
                        key={file.key}
                        variant="outlined"
                        onClick={() =>
                          handleAttachmentClick(
                            selectedAnnouncement.id,
                            file.key
                          )
                        }
                      >
                        {file.file_name}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Stack>
            </form>
          </FormProvider>
        )}
        {!userIsInternal && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="h3">{selectedAnnouncement.title}</Typography>
            <p
              style={{ whiteSpace: 'pre-wrap' }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: previews announcement body with linkified URLs; admin-authored content, not sanitized client-side
              dangerouslySetInnerHTML={{
                __html: parse_and_replace_links(selectedAnnouncement.body || '')
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <div style={{ flex: '1 0 0' }}>
          {type === AnnouncementDialogType.EDIT && (
            <Button color="error" onClick={handleDeleteAnnouncement}>
              Delete Draft
            </Button>
          )}
        </div>
        <Button color="primary" onClick={() => handleCancel()}>
          {type === AnnouncementDialogType.VIEW ? 'Close' : 'Cancel'}
        </Button>
        {type === AnnouncementDialogType.EDIT && (
          <>
            <Button
              disabled={updateAnnouncementLoading || addAttachmentLoading}
              onClick={form.handleSubmit(onSubmit)}
              variant="contained"
            >
              Save Changes
            </Button>
            <Button onClick={handlePublish} variant="contained">
              Publish Announcement
            </Button>
          </>
        )}
        {type === AnnouncementDialogType.CREATE && (
          <Button
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            variant="contained"
            disabled={addAttachmentLoading}
          >
            Create Draft
          </Button>
        )}
      </DialogActions>
      <AnnouncementDeleteModal />
    </Dialog>
  );
};

export default AnnouncementDialog;
